import { useEffect, useState } from "react";
import { tg } from "./telegram.js";

/** Screens are addressed by their canvas number, like "#/App09". Design links already point there. */
export function useRoute(fallback: string): string {
  const read = () => location.hash.match(/^#\/(App\d+[a-z]?|SoundLab)/)?.[1] ?? fallback;
  const [route, setRoute] = useState(read);
  useEffect(() => {
    const on = () => setRoute(read());
    addEventListener("hashchange", on);
    return () => removeEventListener("hashchange", on);
  }, []);
  return route;
}

export function go(screen: string, replace = false): void {
  const hash = `#/${screen}`;
  if (replace) history.replaceState(null, "", hash);
  else history.pushState(null, "", hash);
  dispatchEvent(new HashChangeEvent("hashchange"));
}

/** Screens with no way back: the app's main places and the battle flow, where back is not allowed. */
const NO_BACK = new Set(["App01", "App03", "App09", "App23", "App24", "App11", "App12", "App13", "App14", "App15", "App16"]);

/**
 * Screens after (or on the way into) a battle: back always means the lobby. Following browser
 * history from here could land on a battle that is already over.
 */
const BACK_TO_LOBBY = new Set(["App10", "App10w", "App17", "App18", "App19", "App22", "App22b", "App22c", "App36", "App50", "App52", "App72", "App73"]);

/** After a deposit or withdrawal is sent, back goes to the wallet, never to the form that sent it. */
const BACK_TO_WALLET = new Set(["App28a", "App55", "App56", "App70", "App71", "App75"]);

/** A screen that must do something on Back (like ending a search) sets this while it is shown. */
let backOverride: (() => void) | null = null;
export function setBackOverride(fn: (() => void) | null): void {
  backOverride = fn;
}

/** Telegram draws the Back button itself; we only say when it shows and what it does. */
export function useTelegramBack(route: string): void {
  useEffect(() => {
    const back = tg?.BackButton;
    if (!back) return;
    const onBack = () =>
      backOverride ? backOverride() : BACK_TO_LOBBY.has(route) ? go("App09", true) : BACK_TO_WALLET.has(route) ? go("App28", true) : history.back();
    if (NO_BACK.has(route)) back.hide();
    else back.show();
    back.onClick(onBack);
    return () => back.offClick(onBack);
  }, [route]);
}
